import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const WS_URL = API_URL.replace('http', 'ws').replace('https', 'wss') + '/ws';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const listenersRef = useRef(new Map());
  const mountedRef = useRef(true); // ✅ Ajouté pour suivre le mount
  const connectionAttemptRef = useRef(0); // ✅ Pour éviter double connexion
  
  const [isConnected, setIsConnected] = useState(false);
  const maxReconnectAttempts = 10; // ✅ Augmenté pour dev mode
  const reconnectDelay = 3000; // ✅ Réduit à 3s

  const connect = () => {
    // ✅ Vérifier si le composant est toujours monté
    if (!mountedRef.current) {
      console.log('⚠️ Component unmounted, aborting connection');
      return;
    }

    // ✅ Ne pas reconnecter si déjà connecté
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('✅ WebSocket already connected');
      return;
    }

    // ✅ Ne pas reconnecter si en cours de connexion
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log('⏳ WebSocket connection already in progress');
      return;
    }

    // ✅ Limite atteinte
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.error(`❌ Max reconnection attempts (${maxReconnectAttempts}) reached`);
      return;
    }

    // ✅ Éviter les doubles connexions simultanées
    const currentAttempt = ++connectionAttemptRef.current;

    try {
      console.log(`🔌 Connecting to WebSocket: ${WS_URL}`);
      console.log(`   Attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts}`);
      
      wsRef.current = new WebSocket(WS_URL);

      wsRef.current.onopen = () => {
        // ✅ Vérifier que c'est toujours la bonne tentative
        if (currentAttempt !== connectionAttemptRef.current) {
          console.log('⚠️ Stale connection, closing');
          wsRef.current?.close();
          return;
        }

        console.log('✅ WebSocket connecté');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0; // ✅ Reset les tentatives
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 Message WebSocket reçu:', data.type || 'unknown');

          // Distribuer aux listeners
          listenersRef.current.forEach((callback, id) => {
            try {
              callback(data);
            } catch (err) {
              console.error(`❌ Error in listener "${id}":`, err);
            }
          });
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log(`🔌 WebSocket fermé. Code: ${event.code}, Reason: ${event.reason}`);
        setIsConnected(false);
        wsRef.current = null;

        // ✅ Ne pas reconnecter si unmounted
        if (!mountedRef.current) {
          console.log('⚠️ Component unmounted, no reconnection');
          return;
        }

        // ✅ Reconnecter avec délai exponentiel
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(reconnectDelay * reconnectAttemptsRef.current, 30000); // Max 30s
          
          console.log(`🔄 Reconnexion dans ${delay/1000}s... (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }

          reconnectTimeoutRef.current = setTimeout(connect, delay);
        } else {
          console.error('❌ Max tentatives atteintes. Utilisez reconnect() pour réessayer.');
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        // L'événement close sera déclenché après
      };

    } catch (error) {
      console.error('❌ Erreur création WebSocket:', error);
      setIsConnected(false);

      if (mountedRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++;
        reconnectTimeoutRef.current = setTimeout(connect, reconnectDelay);
      }
    }
  };

  // ✅ Fonction de reconnexion manuelle qui reset le compteur
  const manualReconnect = () => {
    console.log('🔄 Manual reconnection requested');
    reconnectAttemptsRef.current = 0; // Reset le compteur
    
    // Fermer la connexion existante si elle existe
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    // Reconnecter
    connect();
  };

  useEffect(() => {
    mountedRef.current = true;

    // ✅ Délai pour éviter les double-mounts de React StrictMode
    const timer = setTimeout(() => {
      if (mountedRef.current) {
        connect();
      }
    }, 300); // Augmenté à 300ms

    return () => {
      console.log('🧹 Cleanup: Unmounting WebSocketProvider');
      mountedRef.current = false;

      // Nettoyer le timer
      clearTimeout(timer);

      // Nettoyer le timeout de reconnexion
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Fermer la connexion WebSocket
      if (wsRef.current) {
        try {
          if (wsRef.current.readyState === WebSocket.OPEN || 
              wsRef.current.readyState === WebSocket.CONNECTING) {
            wsRef.current.close(1000, 'Component unmounting');
          }
        } catch (err) {
          console.error('Error closing WebSocket:', err);
        }
        wsRef.current = null;
      }

      // Nettoyer les listeners
      listenersRef.current.clear();
    };
  }, []); // ✅ Dépendances vides = une seule exécution

  // Subscribe to messages
  const subscribe = (id, callback) => {
    if (!callback) {
      console.warn(`⚠️ No callback provided for listener "${id}"`);
      return () => {};
    }

    listenersRef.current.set(id, callback);
    console.log(`📝 Listener "${id}" subscribed. Total: ${listenersRef.current.size}`);

    // Return unsubscribe function
    return () => {
      listenersRef.current.delete(id);
      console.log(`🗑️ Listener "${id}" unsubscribed. Remaining: ${listenersRef.current.size}`);
    };
  };

  // Send message
  const sendMessage = (data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      wsRef.current.send(message);
      console.log('📤 Message envoyé:', data);
      return true;
    } else {
      console.error('❌ WebSocket non connecté. État:', wsRef.current?.readyState);
      return false;
    }
  };

  const value = {
    isConnected,
    subscribe,
    sendMessage,
    reconnect: manualReconnect // ✅ Utiliser la version qui reset le compteur
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook personnalisé pour utiliser le WebSocket
export const useWebSocket = (componentName, messageHandler) => {
  const context = useContext(WebSocketContext);
  const handlerRef = useRef(messageHandler);
  
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }

  // ✅ Garder la référence à jour sans recréer la subscription
  useEffect(() => {
    handlerRef.current = messageHandler;
  }, [messageHandler]);

  useEffect(() => {
    if (!componentName) {
      console.warn('⚠️ useWebSocket called without componentName');
      return;
    }

    // ✅ Wrapper stable qui utilise toujours le dernier handler
    const stableHandler = (data) => {
      if (handlerRef.current) {
        handlerRef.current(data);
      }
    };

    // S'abonner
    const unsubscribe = context.subscribe(componentName, stableHandler);

    // Se désabonner au unmount
    return unsubscribe;
  }, [componentName, context]); // ✅ messageHandler retiré des deps

  return {
    isConnected: context.isConnected,
    sendMessage: context.sendMessage,
    reconnect: context.reconnect
  };
};

export default WebSocketContext;